/**
 * SetupWizard Component
 * Full-screen modal wizard for first-time user setup
 */

import React, { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Loader2, X } from 'lucide-react';
import { useSetupWizard } from './hooks/useSetupWizard';
import { SETUP_WIZARD_STEPS } from './types';
import { WelcomeStep } from './steps/WelcomeStep';
import { ProfileStep } from './steps/ProfileStep';
import { EntityStep } from './steps/EntityStep';
import { CompleteStep } from './steps/CompleteStep';

interface SetupWizardProps {
  // Called after wizard is completed or skipped
  onComplete?: () => void;
}

export const SetupWizard: React.FC<SetupWizardProps> = ({ onComplete }) => {
  const {
    currentStep,
    totalSteps,
    currentStepConfig,
    isOpen,
    nextStep,
    previousStep,
    skipWizard,
    completeWizard,
    profileData,
    setProfileData,
    entityData,
    setEntityData,
    isLoading,
    isSaving,
    profile,
  } = useSetupWizard();

  // Render current step content
  const stepContent = useMemo(() => {
    switch (currentStepConfig.id) {
      case 'welcome':
        return <WelcomeStep onContinue={nextStep} />;
      case 'profile':
        return (
          <ProfileStep
            data={profileData}
            onChange={setProfileData}
            onContinue={nextStep}
            onBack={previousStep}
          />
        );
      case 'entity':
        return (
          <EntityStep
            data={entityData}
            onChange={setEntityData}
            onContinue={nextStep}
            onBack={previousStep}
            onSkip={nextStep}
          />
        );
      case 'complete':
        return (
          <CompleteStep
            profileData={profileData}
            entityData={entityData}
            onComplete={async () => {
              await completeWizard();
              onComplete?.();
            }}
            isSaving={isSaving}
          />
        );
      default:
        return null;
    }
  }, [
    currentStepConfig.id,
    nextStep,
    previousStep,
    profileData,
    setProfileData,
    entityData,
    setEntityData,
    completeWizard,
    onComplete,
    isSaving,
  ]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <Dialog open={true}>
        <DialogContent className="max-w-2xl">
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-terracotta-600 mb-4" />
            <p className="text-espresso-500">Loading...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Progress bar */}
        <div className="w-full bg-cream-200 dark:bg-espresso-700 h-1 rounded-full overflow-hidden">
          <div
            className="h-full bg-terracotta-600 transition-all duration-300 ease-out"
            style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Header with step indicator */}
        <DialogHeader className="pt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-espresso-400 dark:text-espresso-300">
              Step {currentStep + 1} of {totalSteps}
            </span>
            {currentStep > 0 && currentStep < totalSteps - 1 && (
              <button
                onClick={skipWizard}
                className="text-xs text-espresso-400 hover:text-espresso-600 dark:text-espresso-300 dark:hover:text-cream-100 transition-colors"
              >
                Skip setup
              </button>
            )}
          </div>
          <DialogTitle className="text-xl font-serif text-espresso-900 dark:text-cream-100">
            {currentStepConfig.title}
          </DialogTitle>
          <DialogDescription className="text-espresso-500 dark:text-espresso-300">
            {currentStepConfig.description}
          </DialogDescription>
        </DialogHeader>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto py-4">
          {stepContent}
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 pt-4 border-t border-cream-200 dark:border-espresso-600">
          {SETUP_WIZARD_STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`w-2 h-2 rounded-full transition-colors ${
                index === currentStep
                  ? 'bg-terracotta-600'
                  : index < currentStep
                  ? 'bg-terracotta-300'
                  : 'bg-cream-300 dark:bg-espresso-600'
              }`}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SetupWizard;
